import { useState, type FormEvent } from "react";
import { changePassword, rotateToken } from "@/api/auth";
import { ApiException } from "@/api/client";
import { copyText } from "@/lib/format";
import { useSession } from "@/session/SessionProvider";

export function SettingsPage() {
	const { admin } = useSession();
	const [currentPassword, setCurrentPassword] = useState("");
	const [newPassword, setNewPassword] = useState("");
	const [message, setMessage] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	const [loginUrl, setLoginUrl] = useState<string | null>(null);
	const [copied, setCopied] = useState(false);

	async function onChangePassword(e: FormEvent) {
		e.preventDefault();
		setError(null);
		setMessage(null);
		setBusy(true);
		try {
			await changePassword(currentPassword, newPassword);
			setMessage("Password updated.");
			setCurrentPassword("");
			setNewPassword("");
		} catch (err) {
			setError(
				err instanceof ApiException ? err.message : "Update failed",
			);
		} finally {
			setBusy(false);
		}
	}

	async function onRotate() {
		if (
			!confirm(
				"Rotate the access token? The old login link will stop working.",
			)
		) {
			return;
		}
		setError(null);
		setMessage(null);
		setBusy(true);
		try {
			const res = await rotateToken();
			setLoginUrl(res.loginUrl);
			setMessage("New access token created — copy the link now.");
		} catch (err) {
			setError(
				err instanceof ApiException ? err.message : "Rotate failed",
			);
		} finally {
			setBusy(false);
		}
	}

	return (
		<div className="mx-auto max-w-lg space-y-8">
			<div>
				<h1 className="text-xl font-semibold tracking-tight">
					Settings
				</h1>
				<p className="mt-1 text-sm text-fg-muted">
					Admin:{" "}
					<span className="font-mono text-fg">{admin?.email}</span>
				</p>
			</div>

			{message && (
				<div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-800 dark:text-emerald-300">
					{message}
				</div>
			)}
			{error && (
				<div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-300">
					{error}
				</div>
			)}

			<form
				onSubmit={onChangePassword}
				className="space-y-3 rounded-xl border border-border bg-surface-raised p-5"
			>
				<h2 className="text-sm font-medium">Change password</h2>
				<label className="block text-sm">
					<span className="text-fg-muted">Current password</span>
					<input
						type="password"
						required
						value={currentPassword}
						onChange={(e) => setCurrentPassword(e.target.value)}
						className="mt-1 w-full rounded-lg border border-border bg-surface px-3 py-2 outline-none focus:ring-2 focus:ring-accent/40"
					/>
				</label>
				<label className="block text-sm">
					<span className="text-fg-muted">New password</span>
					<input
						type="password"
						required
						minLength={8}
						value={newPassword}
						onChange={(e) => setNewPassword(e.target.value)}
						className="mt-1 w-full rounded-lg border border-border bg-surface px-3 py-2 outline-none focus:ring-2 focus:ring-accent/40"
					/>
				</label>
				<button
					type="submit"
					disabled={busy}
					className="rounded-lg bg-teal-500 px-3 py-2 text-sm font-medium text-white hover:bg-teal-600 disabled:opacity-50"
				>
					Update password
				</button>
			</form>

			<div className="space-y-3 rounded-xl border border-border bg-surface-raised p-5">
				<h2 className="text-sm font-medium">Access token (login link)</h2>
				<p className="text-sm text-fg-muted">
					Bookmarkable one-click login. Shown once after rotate.
				</p>
				<button
					type="button"
					disabled={busy}
					onClick={() => void onRotate()}
					className="rounded-lg border border-border px-3 py-2 text-sm hover:bg-surface disabled:opacity-50"
				>
					Rotate access token
				</button>
				{loginUrl && (
					<div className="space-y-2">
						<p className="break-all font-mono text-xs text-accent">
							{loginUrl}
						</p>
						<button
							type="button"
							className="text-sm text-fg-muted hover:text-fg"
							onClick={async () => {
								const ok = await copyText(loginUrl);
								if (ok) {
									setCopied(true);
									setTimeout(() => setCopied(false), 1500);
								}
							}}
						>
							{copied ? "Copied!" : "Copy link"}
						</button>
					</div>
				)}
			</div>
		</div>
	);
}
